import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import {
  getAccount,
  getBotInfo,
  startKyc,
  type KycStartResponse,
  type KycStatusEnvelope,
} from '../api/client.ts';
import { Topbar } from '../components/topbar.tsx';
import { buildBotDeepLink } from '../lib/bot-info.ts';
import { usePlatform } from '../platform/index.ts';

import { rootRoute } from './__root.tsx';

// Bot @username is effectively immutable per deploy. Cache aggressively
// so the support / refresh CTAs that still build t.me deep-links are
// ready on first paint after the very first fetch in a session.
const BOT_INFO_STALE_MS = 24 * 60 * 60 * 1000;

const BLOCKED_COPY: Readonly<Record<'FAILED' | 'ORPHANED' | 'MIGRATED', string>> = {
  FAILED:
    "We couldn't verify your identity. Contact support to retry: the team can reopen the case.",
  ORPHANED:
    'Your verification record needs admin attention. Contact support and we will resolve it.',
  MIGRATED:
    'Your account moved to a new workspace. Contact support to finish identity verification.',
};

export interface KycPendingSearch {
  readonly status?: KycStatusEnvelope;
}

const isKycStatus = (value: unknown): value is KycStatusEnvelope =>
  value === 'NOT_STARTED' ||
  value === 'IN_PROGRESS' ||
  value === 'PASSED' ||
  value === 'REJECTED' ||
  value === 'UNKNOWN';

// Action discriminator. KYC_START primaries mint a kyc-host session via
// POST /api/miniapp/kyc/start and open the returned URL in the external
// system browser; DEEP_LINK primaries continue to bounce users into the
// bot chat (support handle, generic "open chat" fallback).
interface Variant {
  readonly title: string;
  readonly body: string;
  readonly primary:
    | { readonly kind: 'KYC_START'; readonly label: string }
    | { readonly kind: 'DEEP_LINK'; readonly label: string; readonly payload: string }
    | { readonly kind: 'REFRESH'; readonly label: string };
  readonly secondary?:
    | { readonly kind: 'REFRESH'; readonly label: string }
    | { readonly kind: 'DEEP_LINK'; readonly label: string; readonly payload: string };
  readonly tertiary?: { readonly kind: 'DEEP_LINK'; readonly label: string; readonly payload: string };
}

const VARIANTS: Readonly<Record<KycStatusEnvelope, Variant>> = {
  NOT_STARTED: {
    title: 'Verify your identity',
    body: "Tap below to open identity verification in your browser. It takes a couple of minutes and one document. You'll come back here automatically when you're done.",
    primary: { kind: 'KYC_START', label: 'Start verification' },
  },
  IN_PROGRESS: {
    title: 'Verification in review',
    body: "Your documents are with our reviewers; this usually clears within a few minutes. Didn't finish or need to redo it? Reopen verification. Otherwise tap Refresh status to check.",
    primary: { kind: 'KYC_START', label: 'Open verification' },
    secondary: { kind: 'REFRESH', label: 'Refresh status' },
    tertiary: { kind: 'DEEP_LINK', label: 'Contact support', payload: 'support' },
  },
  REJECTED: {
    title: 'Verification failed',
    body: "We couldn't verify your identity. Our support team can help: open a chat to share more context.",
    primary: { kind: 'DEEP_LINK', label: 'Contact support', payload: 'support' },
  },
  PASSED: {
    // PASSED users should never land here — the api client only fires
    // the redirect event on 403 + KYC_REQUIRED, which the guard does
    // not throw for verified users. Variant exists so the union stays
    // exhaustive; if a user reaches this state it is a routing bug,
    // not a verification state.
    title: 'Verified',
    body: "You're verified. Open the dashboard to use your cards.",
    primary: { kind: 'KYC_START', label: 'Open verification' },
  },
  UNKNOWN: {
    title: 'Identity verification needed',
    body: 'Open the bot chat and tap Verify identity to continue.',
    primary: { kind: 'DEEP_LINK', label: 'Open chat', payload: 'kyc' },
  },
};

const KycPendingPage = (): ReactNode => {
  const platform = usePlatform();
  const navigate = useNavigate();
  const search = kycPendingRoute.useSearch();
  const urlStatus: KycStatusEnvelope = isKycStatus(search.status) ? search.status : 'UNKNOWN';
  const qc = useQueryClient();
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const accountQ = useQuery({
    queryKey: ['account'] as const,
    queryFn: ({ signal }) => getAccount(signal),
  });
  const status: KycStatusEnvelope = accountQ.data?.kycStatus ?? urlStatus;
  const variant = VARIANTS[status];

  useEffect(() => {
    if (accountQ.data?.kycStatus === 'PASSED') {
      void navigate({ to: '/' });
    }
  }, [accountQ.data?.kycStatus, navigate]);

  const botInfoQ = useQuery({
    queryKey: ['bot-info'],
    queryFn: ({ signal }) => getBotInfo(signal),
    staleTime: BOT_INFO_STALE_MS,
    gcTime: BOT_INFO_STALE_MS,
    retry: 2,
    // Fetched whenever any CTA (primary / secondary / tertiary) builds a t.me deep-link.
    enabled:
      variant.primary.kind === 'DEEP_LINK' ||
      variant.secondary?.kind === 'DEEP_LINK' ||
      variant.tertiary !== undefined,
  });
  const botUsername = botInfoQ.data?.username ?? '';

  const startKycM = useMutation({
    mutationFn: () => startKyc(),
    onSuccess: (res: KycStartResponse) => {
      setErrorMessage(null);
      setBlockedMessage(null);
      switch (res.kind) {
        case 'URL':
          platform.openLink(res.url);
          return;
        case 'PASSED':
          qc.clear();
          void navigate({ to: '/' });
          return;
        case 'BLOCKED':
          setBlockedMessage(BLOCKED_COPY[res.reason]);
          return;
      }
    },
    onError: () => {
      setErrorMessage("Couldn't start verification. Please try again.");
    },
  });

  const handleRefresh = (): void => {
    void (async (): Promise<void> => {
      qc.clear();
      try {
        const profile = await getAccount();
        if (profile.kycStatus === 'PASSED') {
          void navigate({ to: '/' });
          return;
        }
        qc.setQueryData(['account'], profile);
      } catch {
        // keep the last-known copy; user can retry
      }
    })();
  };

  const openBotChat = (payload: string): void => {
    const url = buildBotDeepLink(botUsername, payload);
    if (url !== null) platform.openBotLink(url);
  };

  const handlePrimary = (): void => {
    switch (variant.primary.kind) {
      case 'KYC_START':
        startKycM.mutate();
        return;
      case 'REFRESH':
        handleRefresh();
        return;
      case 'DEEP_LINK':
        openBotChat(variant.primary.payload);
        return;
    }
  };

  const handleSecondary = (): void => {
    const secondary = variant.secondary;
    if (secondary === undefined) return;
    if (secondary.kind === 'REFRESH') {
      handleRefresh();
      return;
    }
    openBotChat(secondary.payload);
  };

  const handleTertiary = (): void => {
    if (variant.tertiary !== undefined) openBotChat(variant.tertiary.payload);
  };

  const deepLinkReady = botUsername !== '';
  const primaryReady =
    variant.primary.kind === 'KYC_START'
      ? !startKycM.isPending
      : variant.primary.kind === 'DEEP_LINK'
        ? deepLinkReady
        : true;
  const secondaryReady = variant.secondary?.kind === 'DEEP_LINK' ? deepLinkReady : true;

  return (
    <main className="kyc-pending-main" role="main" aria-live="polite">
      {/* A KYC-pending user's only path to /account, where Logout lives. */}
      <Topbar />
      <div className="kyc-pending-card">
        <h1 className="kyc-pending-title">{variant.title}</h1>
        <p className="kyc-pending-body">{variant.body}</p>
        <button
          type="button"
          className="btn-primary kyc-pending-primary"
          onClick={handlePrimary}
          data-status={status}
          disabled={!primaryReady}
          aria-busy={startKycM.isPending}
        >
          {variant.primary.label}
        </button>
        {variant.secondary !== undefined ? (
          <button
            type="button"
            className="kyc-pending-secondary"
            onClick={handleSecondary}
            disabled={!secondaryReady}
          >
            {variant.secondary.label}
          </button>
        ) : null}
        {variant.tertiary !== undefined ? (
          <button
            type="button"
            className="kyc-pending-secondary"
            onClick={handleTertiary}
            disabled={!deepLinkReady}
          >
            {variant.tertiary.label}
          </button>
        ) : null}
        {blockedMessage !== null ? (
          <p className="kyc-pending-error" role="alert">
            {blockedMessage}
          </p>
        ) : null}
        {errorMessage !== null ? (
          <p className="kyc-pending-error" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </main>
  );
};

export const kycPendingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kyc-pending',
  component: KycPendingPage,
  validateSearch: (raw: Record<string, unknown>): KycPendingSearch =>
    isKycStatus(raw.status) ? { status: raw.status } : {},
});
